import { Link, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/docs/plugins/publishing")({
	component: PublishingPage,
});

function H({ id, children }: { id: string; children: React.ReactNode }) {
	return (
		<h2
			id={id}
			className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mt-14 mb-4 pt-6 border-t border-zinc-100 dark:border-zinc-800/50 scroll-mt-20 first:border-0 first:mt-0 first:pt-0"
		>
			{children}
		</h2>
	);
}

function Pre({ title, children }: { title?: string; children: string }) {
	return (
		<div className="rounded bg-[#0a0a0a] border border-zinc-800 overflow-hidden mb-4">
			{title && (
				<div className="px-3 py-1 text-[9px] text-zinc-500 uppercase tracking-widest border-b border-zinc-800 font-medium">
					{title}
				</div>
			)}
			<pre className="px-3 py-2 overflow-x-auto text-[11px] leading-relaxed text-zinc-300 font-mono">
				{children}
			</pre>
		</div>
	);
}

function PublishingPage() {
	const { t } = useTranslation("docs");
	return (
		<div className="w-full">
			<div className="mb-4">
				<h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
					{t("publishing_title")}
				</h1>
				<p className="mt-1.5 text-[13px] text-zinc-500">
					{t("publishing_intro")}
				</p>
			</div>

			<H id="checklist">{t("publishing_checklist")}</H>
			<ul className="text-[12px] text-zinc-500 space-y-1.5 list-disc list-inside mb-4">
				<li
					dangerouslySetInnerHTML={{ __html: t("publishing_checklist_wasm") }}
				/>
				<li
					dangerouslySetInnerHTML={{ __html: t("publishing_checklist_toml") }}
				/>
				<li>
					{t("publishing_checklist_account")}{" "}
					<Link
						to="/settings/plugins/developer"
						className="text-primary hover:underline"
					>
						{t("publishing_checklist_portal")}
					</Link>
				</li>
			</ul>

			<H id="manifest-requirements">{t("publishing_manifest_requirements")}</H>
			<Pre title="plugin.toml">{`[plugin]
id = "com.yourorg.plugin-name"       # reverse-domain, глобально уникальный
name = "My Plugin"
version = "1.0.0"                     # semver
description = "Краткое описание"      # до 200 символов
author = "Имя или организация"
license = "MIT"                       # MIT, Apache-2.0, GPL-3.0, UNLICENSED
min_abi_version = "1.0"

[plugin.metadata]
category = "enrichment"               # enrichment, integration, automation, analytics, ai
tags = ["metal", "weight"]            # до 5 тегов
icon = "hammer"                       # Lucide icon

[capabilities]
# только необходимые

[[extensions]]
point = "enrichment.extract"
handler = "my_handler"
priority = 50                         # 100 = системный, 50 = обычный, 10 = fallback`}</Pre>

			<H id="via-api">{t("publishing_via_api")}</H>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{ __html: t("publishing_via_api_desc") }}
			/>
			<Pre title="shell">{`grpcurl -plaintext \\
  -H "Authorization: Bearer $TOKEN" \\
  -d '{
    "slug": "my-plugin",
    "name": "My Plugin",
    "version": "1.0.0",
    "manifest_toml": "...",
    "wasm_bytes": "<base64>",
    "description": "...",
    "category_slug": "enrichment"
  }' \\
  localhost:50051 omnios.v1.PluginService/PublishPlugin`}</Pre>

			<H id="via-ui">{t("publishing_via_ui")}</H>
			<ol className="text-[12px] text-zinc-500 space-y-1.5 list-decimal list-inside mb-4">
				<li>
					{t("publishing_ui_step1")}{" "}
					<Link
						to="/settings/plugins/developer"
						className="text-primary hover:underline"
					>
						{t("publishing_ui_settings")}
					</Link>
				</li>
				<li>{t("publishing_ui_step2")}</li>
				<li dangerouslySetInnerHTML={{ __html: t("publishing_ui_step3") }} />
				<li>{t("publishing_ui_step4")}</li>
				<li>{t("publishing_ui_step5")}</li>
			</ol>

			<H id="update">{t("publishing_update")}</H>
			<ol className="text-[12px] text-zinc-500 space-y-1.5 list-decimal list-inside mb-4">
				<li
					dangerouslySetInnerHTML={{ __html: t("publishing_update_step1") }}
				/>
				<li>{t("publishing_update_step2")}</li>
				<li
					dangerouslySetInnerHTML={{ __html: t("publishing_update_step3") }}
				/>
			</ol>
			<div className="rounded border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50 px-3 py-2 mb-4">
				<p className="text-[12px] text-zinc-500">
					{t("publishing_update_note")}
				</p>
			</div>

			<H id="system-plugins">{t("publishing_system_plugins")}</H>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				{t("publishing_system_desc")}
			</p>
			<Pre title="rust">{`// SystemPluginConfig (backend)
SystemPluginMapping {
    name: "metal-ru",
    feature_slug: Some("metal"),
    is_required: true,
}`}</Pre>

			<H id="recommendations">{t("publishing_recommendations")}</H>
			<ul className="text-[12px] text-zinc-500 space-y-1.5 list-disc list-inside mb-4">
				<li>
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("publishing_rec_min_caps")}
					</strong>{" "}
					— {t("publishing_rec_min_caps_desc")}
				</li>
				<li>
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("publishing_rec_graceful")}
					</strong>{" "}
					—{" "}
					<span
						dangerouslySetInnerHTML={{
							__html: t("publishing_rec_graceful_desc"),
						}}
					/>
				</li>
				<li>
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("publishing_rec_kv")}
					</strong>{" "}
					—{" "}
					<span
						dangerouslySetInnerHTML={{ __html: t("publishing_rec_kv_desc") }}
					/>
				</li>
				<li>
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("publishing_rec_size")}
					</strong>{" "}
					—{" "}
					<span
						dangerouslySetInnerHTML={{ __html: t("publishing_rec_size_desc") }}
					/>
				</li>
				<li>
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("publishing_rec_priority")}
					</strong>{" "}
					— {t("publishing_rec_priority_desc")}
				</li>
			</ul>

			<div className="mt-14 pt-4 border-t border-zinc-100 dark:border-zinc-800/50 text-center">
				<p className="text-[10px] text-zinc-400 tracking-wider uppercase">
					OmniOS Plugin Publishing
				</p>
			</div>
		</div>
	);
}
